/**
 * `network` store —— 中继状态、每对端通道状态、在线目录、隐身开关。
 *
 * UI 只允许从这里（或 `@shared/types`）取 `ConnStatus`，**禁止 import `net/*`**（ARCH §8.4.4）。
 */

import { create } from 'zustand';
import type { ConnStatus, OnlinePeer } from '@shared/types';
import type { RelayStatus } from '@/net/connection';

export type RelayPhase = RelayStatus | 'idle';

export interface RelaySnapshot {
  peerId: string;
  addr: string;
  status: RelayPhase;
  latencyMs?: number;
}

export interface NetworkState {
  relay: RelaySnapshot;
  /** peerId → 通道状态（状态灯） */
  transportByPeer: Record<string, ConnStatus>;
  online: OnlinePeer[];
  stealth: boolean;
  lastOnlineRefreshAt?: number;

  setRelay(snapshot: Partial<RelaySnapshot>): void;
  setPeerStatus(peerId: string, status: ConnStatus, latencyMs?: number): void;
  setOnline(peers: OnlinePeer[]): void;
  setStealth(stealth: boolean): void;
  reset(): void;
}

export const useNetworkStore = create<NetworkState>((set) => ({
  relay: { peerId: '', addr: '', status: 'idle' },
  transportByPeer: {},
  online: [],
  stealth: false,

  setRelay: (snapshot) =>
    set((state) => ({
      relay: {
        peerId: snapshot.peerId ?? state.relay.peerId,
        addr: snapshot.addr ?? state.relay.addr,
        status: snapshot.status ?? state.relay.status,
        latencyMs: snapshot.latencyMs ?? state.relay.latencyMs,
      },
    })),

  setPeerStatus: (peerId, status, latencyMs) =>
    set((state) => ({
      transportByPeer: { ...state.transportByPeer, [peerId]: status },
      relay: latencyMs === undefined ? state.relay : { ...state.relay, latencyMs },
    })),

  setOnline: (peers) => set({ online: peers, lastOnlineRefreshAt: Date.now() }),

  setStealth: (stealth) => set({ stealth }),

  reset: () =>
    set({
      relay: { peerId: '', addr: '', status: 'idle' },
      transportByPeer: {},
      online: [],
      lastOnlineRefreshAt: undefined,
    }),
}));

/** 状态灯文案（UI 唯一可展示的连接状态文本）。 */
export const CONN_STATUS_TEXT: Record<ConnStatus, string> = {
  idle: '未连接',
  connecting: '连接中',
  direct: '直连',
  relay: '经中继',
  offline: '离线',
};

/** 状态灯颜色 token（对应 tailwind.config.ts 中的语义色）。 */
export const CONN_STATUS_COLOR: Record<ConnStatus, string> = {
  idle: 'bg-dim',
  connecting: 'bg-warn',
  direct: 'bg-ok',
  relay: 'bg-accent',
  offline: 'bg-err',
};

/**
 * 顶部全局状态灯：把中继阶段（`RelayPhase`）映射为文案 + 色板。
 *
 * 注意与 `CONN_STATUS_TEXT` 区分：那是「单对端通道态」，这里是「中继链路态」。
 * 文案保持稳定（不掺入延迟），避免心跳刷新时状态灯文字抖动。
 */
export function relayLamp(status: RelayPhase): { label: string; tone: 'ok' | 'warn' | 'err' } {
  switch (status) {
    case 'ready':
    case 'connected':
      return { label: '已连接', tone: 'ok' };
    case 'connecting':
      return { label: '连接中', tone: 'warn' };
    default:
      return { label: '离线', tone: 'err' };
  }
}

export default useNetworkStore;
